import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiClient } from '../api/client';

interface EventTask {
  id: string;
  title: string;
  description: string;
  status: 'ToDo' | 'InProgress' | 'Done' | 'Cancelled';
  priority: 'High' | 'Medium' | 'Low';
  category: 'Weekend' | 'Planning' | 'General' | 'Other';
  dueDate: string;
  completedAt: string | null;
  event: { id: string; name: string } | null;
  subcommittee: { id: string; name: string };
}

interface EventTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const STATUS_OPTIONS: Array<'ToDo' | 'InProgress' | 'Done'> = ['ToDo', 'InProgress', 'Done'];

const STATUS_LABELS: Record<string, string> = {
  ToDo: 'To Do',
  InProgress: 'In Progress',
  Done: 'Done',
  Cancelled: 'Cancelled',
};

const PRIORITY_COLORS: Record<string, string> = {
  High: '#dc2626',
  Medium: '#d97706',
  Low: '#16a34a',
};

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  panel: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '28px',
    width: '720px',
    maxWidth: '90vw',
    maxHeight: '85vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
  },
  panelMobile: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    width: '100%',
    maxWidth: '100vw',
    maxHeight: '95vh',
    overflowY: 'auto' as const,
    position: 'relative' as const,
    margin: '8px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
  },
  title: {
    fontSize: '18px',
    fontWeight: 700,
    color: '#1a6b3c',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#666',
    padding: '4px 8px',
    borderRadius: '4px',
    lineHeight: 1,
  },
  filters: {
    display: 'flex',
    gap: '0.75rem',
    marginBottom: '1.25rem',
    flexWrap: 'wrap' as const,
  },
  filterSelect: {
    padding: '6px 10px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '0.8rem',
    color: '#374151',
    backgroundColor: '#fff',
    cursor: 'pointer',
  },
  groupHeader: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#1a6b3c',
    marginTop: '1rem',
    marginBottom: '0.5rem',
    paddingBottom: '4px',
    borderBottom: '1px solid #e5e7eb',
  },
  taskCard: {
    padding: '0.85rem',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    marginBottom: '0.5rem',
    backgroundColor: '#fafafa',
  },
  taskTitle: {
    fontSize: '0.88rem',
    fontWeight: 600,
    color: '#111827',
    marginBottom: '4px',
  },
  taskDescription: {
    fontSize: '0.78rem',
    color: '#6b7280',
    marginBottom: '6px',
    lineHeight: 1.4,
  },
  taskMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    flexWrap: 'wrap' as const,
    fontSize: '0.75rem',
    color: '#6b7280',
  },
  priorityBadge: {
    display: 'inline-block',
    padding: '2px 8px',
    borderRadius: '10px',
    fontSize: '0.7rem',
    fontWeight: 600,
    color: '#fff',
  },
  statusControl: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginTop: '8px',
  },
  statusBtn: {
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #d1d5db',
    fontSize: '0.72rem',
    cursor: 'pointer',
    backgroundColor: '#fff',
    color: '#374151',
    fontWeight: 500,
    transition: 'all 0.15s',
  },
  statusBtnActive: {
    padding: '4px 10px',
    borderRadius: '4px',
    border: '1px solid #1a6b3c',
    fontSize: '0.72rem',
    cursor: 'default',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontWeight: 600,
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#6b7280',
    fontSize: '0.9rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    color: '#6b7280',
    fontSize: '0.9rem',
  },
};

export function EventTaskModal({ isOpen, onClose }: EventTaskModalProps) {
  const [tasks, setTasks] = useState<EventTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Filters
  const [filterSubcommittee, setFilterSubcommittee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const contentRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Store previously focused element
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Focus modal on open
  useEffect(() => {
    if (isOpen && contentRef.current) {
      contentRef.current.focus();
    }
  }, [isOpen]);

  // Restore focus on close
  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
    }
  }, [isOpen]);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterSubcommittee) params.set('subcommitteeId', filterSubcommittee);
      if (filterStatus) params.set('status', filterStatus);
      if (filterCategory) params.set('category', filterCategory);
      const query = params.toString();
      const url = `/event-tasks/my-tasks${query ? `?${query}` : ''}`;
      const data = await apiClient<EventTask[]>(url);
      setTasks(data);
    } catch {
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }, [filterSubcommittee, filterStatus, filterCategory]);

  useEffect(() => {
    if (isOpen) {
      fetchTasks();
    }
  }, [isOpen, fetchTasks]);

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    setUpdatingId(taskId);
    try {
      const updated = await apiClient<EventTask>(`/event-tasks/${taskId}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      setTasks((prev) => prev.map((t) => (t.id === taskId ? updated : t)));
    } catch {
      // Silently fail; user can retry
    } finally {
      setUpdatingId(null);
    }
  };

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    },
    [onClose],
  );

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  // Group tasks by subcommittee name
  const grouped: Record<string, EventTask[]> = {};
  tasks.forEach((task) => {
    const name = task.subcommittee?.name || 'Unassigned';
    if (!grouped[name]) grouped[name] = [];
    grouped[name].push(task);
  });

  // Derive unique subcommittee names for filter dropdown
  const subcommitteeNames = Array.from(
    new Map(tasks.map((t) => [t.subcommittee?.id, t.subcommittee?.name])).entries(),
  ).filter(([id]) => !!id);

  return (
    <div
      style={styles.overlay}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="event-task-modal-title"
      onKeyDown={handleKeyDown}
    >
      <div
        ref={contentRef}
        style={isMobile ? styles.panelMobile : styles.panel}
        tabIndex={-1}
      >
        <div style={styles.header}>
          <h3 id="event-task-modal-title" style={styles.title}>
            My Event Tasks
          </h3>
          <button
            style={styles.closeBtn}
            onClick={onClose}
            aria-label="Close modal"
            type="button"
          >
            ✕
          </button>
        </div>

        {/* Filters */}
        <div style={styles.filters}>
          <select
            style={styles.filterSelect}
            value={filterSubcommittee}
            onChange={(e) => setFilterSubcommittee(e.target.value)}
            aria-label="Filter by subcommittee"
          >
            <option value="">All Subcommittees</option>
            {subcommitteeNames.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>

          <select
            style={styles.filterSelect}
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="ToDo">To Do</option>
            <option value="InProgress">In Progress</option>
            <option value="Done">Done</option>
            <option value="Cancelled">Cancelled</option>
          </select>

          <select
            style={styles.filterSelect}
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            aria-label="Filter by category"
          >
            <option value="">All Categories</option>
            <option value="Weekend">Weekend</option>
            <option value="Planning">Planning</option>
            <option value="General">General</option>
            <option value="Other">Other</option>
          </select>
        </div>

        {/* Content */}
        {loading && <div style={styles.loading}>Loading tasks…</div>}
        {error && <div style={{ ...styles.emptyState, color: '#dc2626' }}>{error}</div>}
        {!loading && !error && tasks.length === 0 && (
          <div style={styles.emptyState}>No tasks assigned to you.</div>
        )}

        {!loading &&
          !error &&
          Object.keys(grouped)
            .sort()
            .map((subcommitteeName) => (
              <div key={subcommitteeName}>
                <div style={styles.groupHeader}>{subcommitteeName}</div>
                {grouped[subcommitteeName].map((task) => (
                  <div key={task.id} style={styles.taskCard}>
                    <div style={styles.taskTitle}>{task.title}</div>
                    <div style={styles.taskDescription}>{task.description}</div>
                    <div style={styles.taskMeta}>
                      <span
                        style={{
                          ...styles.priorityBadge,
                          backgroundColor: PRIORITY_COLORS[task.priority] || '#6b7280',
                        }}
                      >
                        {task.priority}
                      </span>
                      {task.event && <span>📅 {task.event.name}</span>}
                      <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
                      <span>Status: {STATUS_LABELS[task.status]}</span>
                    </div>

                    {/* Status update controls */}
                    {task.status !== 'Cancelled' && (
                      <div style={styles.statusControl}>
                        {STATUS_OPTIONS.map((s) => (
                          <button
                            key={s}
                            type="button"
                            style={
                              task.status === s
                                ? styles.statusBtnActive
                                : styles.statusBtn
                            }
                            disabled={task.status === s || updatingId === task.id}
                            onClick={() => handleStatusChange(task.id, s)}
                            aria-label={`Set status to ${STATUS_LABELS[s]}`}
                          >
                            {STATUS_LABELS[s]}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}
      </div>
    </div>
  );
}
